// Libs
import classNames from 'classnames/bind';
import { Box, Button, Dialog, DialogContent, DialogTitle, List, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
// Components, Layouts, Pages
import { DocumentItem } from '~/components/module/document';
import { Loading } from '~/components/common';
// Others
import { IDocument } from '~/utils/interface/document';
import { IQueryParamListFile } from '~/utils/interface/file';
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_LIMIT_1000,
  DEFAULT_NUMBER_THREE,
  DEFAULT_NUMBER_ZERO,
  EMPTY_STRING,
} from '~/utils/constants/common';
import { BusinessTypeEnum } from '~/utils/enum/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getListFile } from '~/thunks/file/fileThunk';
// Styles, images, icons
import styles from './ModalAttachFiles.module.scss';

type Props = {
  isOpen: boolean;
  data?: IDocument[];
  onClose: () => void;
  onSave: (data: IDocument[]) => void;
};

const cx = classNames.bind(styles);

const ModalAttachFiles = (props: Props) => {
  //#region Destructuring Props
  const { isOpen, data, onClose, onSave } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/invoice', 'common/base']);
  const { id: jobId } = useParams();
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoadingFile, setIsLoadingFile] = useState<boolean>(false);
  const [fileList, setFileList] = useState<IDocument[]>([]);
  const [fileListSelected, setFileListSelected] = useState<IDocument[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>(EMPTY_STRING);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!data) return;

    setFileListSelected(data);
  }, [data]);

  useEffect(() => {
    handelGetListFile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //#endregion Implement Hook

  //#region Handle Function
  const handelGetListFile = () => {
    if (!jobId) return;
    const params: IQueryParamListFile = {
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_1000,
      businessId: jobId,
      businessType: BusinessTypeEnum.JOB,
    };

    setIsLoadingFile(true);

    dispatch(getListFile(params))
      .unwrap()
      .then((res) => {
        const { responses } = res?.data;
        if (!responses) return;

        setFileList(responses);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoadingFile(false);
      });
  };

  const handleCancelModal = () => {
    setErrorMessage(EMPTY_STRING);
    setFileListSelected([]);
    onClose();
  };

  const handleClickItemFile = (item: IDocument) => {
    setFileListSelected((prev) => {
      const isExist = prev.some((file) => file.id === item.id);
      if (isExist) {
        return prev.filter((file) => file.id !== item.id);
      } else {
        return [...prev, item];
      }
    });
  };

  const handleSaveModal = () => {
    if (fileListSelected.length > DEFAULT_NUMBER_THREE) {
      setErrorMessage(t('modal_attach_file_message_max_file'));
      return;
    }

    if (fileListSelected.length === DEFAULT_NUMBER_ZERO) {
      setErrorMessage(t('modal_attach_file_message_min_file'));
      return;
    }

    setErrorMessage(EMPTY_STRING);
    onSave && onSave(fileListSelected);
  };
  //#endregion Handle Function

  return (
    <Dialog id='modalAttachFiles' open={isOpen} fullWidth maxWidth={'md'} onClose={handleCancelModal}>
      <DialogTitle>{t('modal_attach_file_title')}</DialogTitle>

      <DialogContent dividers={true}>
        <Box component={'div'} className={cx('content')}>
          <List className={cx('listDocument')}>
            {fileList?.map((file) => (
              <DocumentItem
                key={file.id}
                document={file}
                isAction={true}
                isSelected={fileListSelected?.some((item) => item.id === file.id)}
                onclick={handleClickItemFile}
              />
            ))}
          </List>

          {fileList?.length === DEFAULT_NUMBER_ZERO && (
            <Typography className={cx('noDataAvailable')}>
              {t('common_no_data_available', { ns: 'common/base' })}
            </Typography>
          )}
        </Box>

        <Box className={cx('button')}>
          <Typography className={cx('errorMessage')}>{errorMessage}</Typography>

          <Box className={cx('buttonGroup')}>
            <Button variant='outlined' onClick={handleCancelModal}>
              {t('common_action_cancel', { ns: 'common/base' })}
            </Button>

            <Button variant='contained' onClick={handleSaveModal}>
              {t('common_action_save', { ns: 'common/base' })}
            </Button>
          </Box>
        </Box>
      </DialogContent>

      {isLoadingFile && <Loading />}
    </Dialog>
  );
};

export default ModalAttachFiles;
